import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Layout,
  List,
  Result,
  Skeleton,
  Space,
  Spin,
  Steps,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseOutlined,
  CommentOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  MinusOutlined,
  PhoneOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
  ShoppingCartOutlined,
  UserOutlined,
} from "@ant-design/icons";
import API_CONFIG from "../config/api.js";
import "./PublicMenu.css";

const { Content } = Layout;
const { Text, Title } = Typography;
const { TextArea } = Input;

type MenuThemeConfig = {
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  colors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
  };
};

type PublicMenuInfo = {
  menu_name: string;
  menu_slug: string;
  description?: string | null;
  logo_url?: string | null;
  theme_config?: MenuThemeConfig | null;
  allow_self_service_orders: boolean;
  auto_send_to_kitchen: boolean;
  require_table_token: boolean;
  require_customer_phone: boolean;
  default_service_mode: "dine_in" | "takeaway" | "delivery";
  company?: {
    name?: string;
    phone?: string | null;
    email?: string | null;
    address?: string | null;
    logo_url?: string | null;
  };
  warehouse?: {
    name?: string;
  };
  table?: {
    id: number;
    label: string;
    seats?: number | null;
  } | null;
  table_token_valid?: boolean;
};

type MenuCategory = {
  id: number;
  name: string;
};

type MenuProduct = {
  id: number;
  name: string;
  description?: string | null;
  image_url?: string | null;
  gallery_images?: string[];
  category_id?: number | null;
  category_name?: string | null;
  final_price: number;
  online_min_quantity?: number | null;
  online_max_quantity?: number | null;
  stock_management_mode?: "standard" | "pre_order" | "unlimited";
  stock_quantity?: number;
  stock_status?: "en_stock" | "rupture" | "sur_commande" | "disponible";
  available_for_purchase?: boolean;
  is_featured?: boolean;
};

type CartItem = {
  product: MenuProduct;
  quantity: number;
  notes?: string;
};

type OrderFormValues = {
  customer_name?: string;
  customer_phone?: string;
  table_label?: string;
  guest_count?: number;
  notes?: string;
};

type PublicOrderResponse = {
  order_id: number;
  order_number: string;
  public_order_token: string;
  flow_status: string;
  kitchen_status: string;
  total: number;
  total_items: number;
  table_label?: string | null;
  auto_sent_to_kitchen?: boolean;
};

type PublicOrderStatus = {
  order: {
    order_number: string;
    order_date: string;
    service_mode: string;
    flow_status: string;
    kitchen_status: string;
    table_label?: string | null;
    guest_count?: number;
    customer_name?: string | null;
    total: number;
    total_items: number;
    total_quantity: number;
    created_at: string;
    updated_at: string;
    items: Array<{
      product_id: number;
      product_name: string;
      quantity: number;
      unit_price: number;
      subtotal: number;
      line_status: string;
      kitchen_ticket_status?: string | null;
      unit_short_name?: string | null;
      notes?: string | null;
    }>;
  };
};

type RecentPublicOrder = {
  order_number: string;
  public_order_token: string;
  flow_status?: string;
  kitchen_status?: string;
  total: number;
  total_items: number;
  table_label?: string | null;
  table_token?: string;
  created_at: string;
};

type PublicOrderStage =
  | "received"
  | "kitchen"
  | "ready"
  | "completed"
  | "blocked"
  | "cancelled";

const MENU_API = API_CONFIG.PUBLIC_MENU_API;
const RECENT_ORDER_LIMIT = 5;
const RECENT_ORDER_MAX_AGE_MS = 48 * 60 * 60 * 1000;

const currencyFormatter = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "XOF",
  maximumFractionDigits: 0,
});

const publicStageMeta: Record<
  PublicOrderStage,
  { color: string; label: string; description: string; step: number }
> = {
  received: {
    color: "blue",
    label: "Commande recue",
    description: "Votre commande a bien ete transmise au restaurant.",
    step: 0,
  },
  kitchen: {
    color: "gold",
    label: "En preparation",
    description: "Votre commande est en cours de traitement.",
    step: 1,
  },
  ready: {
    color: "green",
    label: "Prete",
    description: "Votre commande est prete ou en finalisation de service.",
    step: 2,
  },
  completed: {
    color: "green",
    label: "Terminee",
    description: "Votre commande est terminee.",
    step: 3,
  },
  blocked: {
    color: "red",
    label: "A verifier",
    description: "Le restaurant verifie un article de votre commande.",
    step: 1,
  },
  cancelled: {
    color: "red",
    label: "Annulee",
    description: "Votre commande ne sera pas preparee.",
    step: 0,
  },
};

const getStoredJson = <T,>(key: string, fallback: T): T => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch (error) {
    return fallback;
  }
};

const createClientId = () => {
  if (window.crypto && "randomUUID" in window.crypto) {
    return window.crypto.randomUUID();
  }

  return `client_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

const formatMoney = (value: number | string | null | undefined) =>
  currencyFormatter.format(Number(value || 0));

const clampQuantity = (
  quantity: number,
  product: MenuProduct,
  fallback = 1
) => {
  const min = Number(product.online_min_quantity || 1);
  const max = Number(product.online_max_quantity || 0);
  const next = Number.isFinite(quantity) ? quantity : fallback;
  const withMin = Math.max(next, min > 0 ? min : 1);
  return max > 0 ? Math.min(withMin, max) : withMin;
};

const pruneRecentOrders = (orders: RecentPublicOrder[]) => {
  const now = Date.now();
  return orders
    .filter((order) => {
      const createdAt = new Date(order.created_at).getTime();
      return Number.isFinite(createdAt) && now - createdAt <= RECENT_ORDER_MAX_AGE_MS;
    })
    .slice(0, RECENT_ORDER_LIMIT);
};

const getPublicOrderStage = (
  flowStatus?: string,
  kitchenStatus?: string
): PublicOrderStage => {
  const flow = String(flowStatus || "").toLowerCase();
  const kitchen = String(kitchenStatus || "").toLowerCase();

  if (flow === "cancelled" || kitchen === "cancelled") return "cancelled";
  if (kitchen === "blocked") return "blocked";
  if (flow === "closed" || flow === "served") return "completed";
  if (flow === "ready" || kitchen === "converted") return "ready";
  if (
    flow === "sent_to_kitchen" ||
    flow === "in_preparation" ||
    kitchen === "ready" ||
    kitchen === "partial"
  ) {
    return "kitchen";
  }

  return "received";
};

const productImageFallback = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("");

const ProductThumb: React.FC<{ product: MenuProduct; size?: "small" | "large" }> = ({
  product,
  size = "large",
}) => (
  <div className={`public-menu__product-thumb public-menu__product-thumb--${size}`}>
    {product.image_url ? (
      <img src={product.image_url} alt={product.name} loading="lazy" />
    ) : (
      <span>{productImageFallback(product.name)}</span>
    )}
  </div>
);

const PublicMenu: React.FC = () => {
  const { menuSlug, publicOrderToken } = useParams<{
    menuSlug: string;
    publicOrderToken?: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tableToken = searchParams.get("t") || searchParams.get("table") || "";
  const isStatusPage = Boolean(publicOrderToken);

  const [form] = Form.useForm<OrderFormValues>();
  const [menuInfo, setMenuInfo] = useState<PublicMenuInfo | null>(null);
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [products, setProducts] = useState<MenuProduct[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | "all">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [menuLoading, setMenuLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartLoaded, setCartLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusData, setStatusData] = useState<PublicOrderStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [recentOrders, setRecentOrders] = useState<RecentPublicOrder[]>([]);

  const cartStorageKey = useMemo(
    () => `restaurant_menu_cart_${menuSlug || "unknown"}`,
    [menuSlug]
  );

  const sessionStorageKey = useMemo(
    () => `restaurant_menu_session_${menuSlug || "unknown"}`,
    [menuSlug]
  );

  const recentOrdersStorageKey = useMemo(
    () => `restaurant_menu_orders_${menuSlug || "unknown"}`,
    [menuSlug]
  );

  const sessionId = useMemo(() => {
    if (!menuSlug) return "";
    const existing = window.localStorage.getItem(sessionStorageKey);
    if (existing) return existing;
    const next = createClientId();
    window.localStorage.setItem(sessionStorageKey, next);
    return next;
  }, [menuSlug, sessionStorageKey]);

  const themeStyle = useMemo(() => {
    const theme = menuInfo?.theme_config;
    const primary =
      theme?.primary_color || theme?.colors?.primary || "#173f5f";
    const accent =
      theme?.accent_color || theme?.colors?.accent || "#be4349";
    const secondary =
      theme?.secondary_color || theme?.colors?.secondary || "#f5f8fb";

    return {
      "--menu-primary": primary,
      "--menu-accent": accent,
      "--menu-secondary": secondary,
    } as React.CSSProperties;
  }, [menuInfo]);

  const cartCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems]
  );

  const cartTotal = useMemo(
    () =>
      cartItems.reduce(
        (sum, item) => sum + item.quantity * Number(item.product.final_price || 0),
        0
      ),
    [cartItems]
  );

  const canOrder = Boolean(menuInfo?.allow_self_service_orders);
  const hasValidTableToken = Boolean(tableToken && menuInfo?.table_token_valid);
  const hasInvalidTableToken = Boolean(
    tableToken && menuInfo && !menuInfo.table_token_valid
  );
  const missingRequiredTable = Boolean(
    menuInfo?.require_table_token && !hasValidTableToken
  );
  const needsManualTableLabel = Boolean(
    menuInfo?.default_service_mode === "dine_in" &&
      !hasValidTableToken &&
      !menuInfo?.require_table_token
  );

  const loadMenu = useCallback(async () => {
    if (!menuSlug) return;
    setMenuLoading(true);
    setLoadError(null);

    try {
      const menuUrl = tableToken
        ? `${MENU_API}/${menuSlug}?t=${encodeURIComponent(tableToken)}`
        : `${MENU_API}/${menuSlug}`;
      const response = await fetch(menuUrl);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.details || "Menu digital introuvable.");
      }

      setMenuInfo(data.menu);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Erreur de chargement.");
    } finally {
      setMenuLoading(false);
    }
  }, [menuSlug, tableToken]);

  const loadCategories = useCallback(async () => {
    if (!menuSlug) return;
    try {
      const response = await fetch(`${MENU_API}/${menuSlug}/categories`);
      const data = await response.json();
      if (response.ok) {
        setCategories(Array.isArray(data.categories) ? data.categories : []);
      }
    } catch (error) {
      setCategories([]);
    }
  }, [menuSlug]);

  const loadProducts = useCallback(async () => {
    if (!menuSlug) return;
    setProductsLoading(true);

    try {
      const params = new URLSearchParams({
        page: "1",
        limit: "100",
      });

      if (debouncedSearch) params.append("search", debouncedSearch);
      if (selectedCategoryId !== "all") {
        params.append("category_id", String(selectedCategoryId));
      }

      const response = await fetch(`${MENU_API}/${menuSlug}/products?${params}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.details || "Produits indisponibles.");
      }

      setProducts(Array.isArray(data.products) ? data.products : []);
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : "Erreur lors du chargement du menu."
      );
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  }, [debouncedSearch, menuSlug, selectedCategoryId]);

  const loadStatus = useCallback(async () => {
    if (!menuSlug || !publicOrderToken) return;
    setStatusLoading(true);

    try {
      const response = await fetch(
        `${MENU_API}/${menuSlug}/orders/${publicOrderToken}/status`
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.details || "Commande introuvable.");
      }

      setStatusData(data);
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : "Erreur lors du chargement."
      );
    } finally {
      setStatusLoading(false);
    }
  }, [menuSlug, publicOrderToken]);

  useEffect(() => {
    loadMenu();
    loadCategories();
  }, [loadCategories, loadMenu]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), 240);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!isStatusPage) {
      loadProducts();
    }
  }, [isStatusPage, loadProducts]);

  useEffect(() => {
    if (!isStatusPage) return;
    loadStatus();
    const interval = window.setInterval(loadStatus, 15000);
    return () => window.clearInterval(interval);
  }, [isStatusPage, loadStatus]);

  useEffect(() => {
    if (!menuSlug) return;
    setCartItems(getStoredJson<CartItem[]>(cartStorageKey, []));
    setCartLoaded(true);
  }, [cartStorageKey, menuSlug]);

  useEffect(() => {
    if (!menuSlug) return;
    const storedOrders = getStoredJson<RecentPublicOrder[]>(
      recentOrdersStorageKey,
      []
    );
    const prunedOrders = pruneRecentOrders(storedOrders);
    setRecentOrders(prunedOrders);
    window.localStorage.setItem(
      recentOrdersStorageKey,
      JSON.stringify(prunedOrders)
    );
  }, [menuSlug, recentOrdersStorageKey]);

  useEffect(() => {
    if (!cartLoaded) return;
    window.localStorage.setItem(cartStorageKey, JSON.stringify(cartItems));
  }, [cartItems, cartLoaded, cartStorageKey]);

  const updateCartQuantity = (product: MenuProduct, nextQuantity: number) => {
    if (product.available_for_purchase === false) {
      message.warning("Ce produit est momentanement indisponible.");
      return;
    }

    setCartItems((current) => {
      const existing = current.find((item) => item.product.id === product.id);
      const normalizedQuantity = clampQuantity(nextQuantity, product);

      if (!existing && nextQuantity <= 0) return current;
      if (existing && nextQuantity <= 0) {
        return current.filter((item) => item.product.id !== product.id);
      }

      if (!existing) {
        return [...current, { product, quantity: normalizedQuantity }];
      }

      return current.map((item) =>
        item.product.id === product.id
          ? { ...item, quantity: normalizedQuantity }
          : item
      );
    });
  };

  const getCartQuantity = (productId: number) =>
    cartItems.find((item) => item.product.id === productId)?.quantity || 0;

  const clearCart = () => {
    setCartItems([]);
    window.localStorage.removeItem(cartStorageKey);
  };

  const rememberRecentOrder = useCallback(
    (
      order: {
        order_number: string;
        public_order_token?: string;
        flow_status?: string;
        kitchen_status?: string;
        total: number;
        total_items: number;
        table_label?: string | null;
        created_at?: string;
      },
      tokenOverride?: string
    ) => {
      const orderToken = order.public_order_token || tokenOverride;
      if (!orderToken || !menuSlug) return;

      setRecentOrders((current) => {
        const nextOrder: RecentPublicOrder = {
          order_number: order.order_number,
          public_order_token: orderToken,
          flow_status: order.flow_status,
          kitchen_status: order.kitchen_status,
          total: order.total,
          total_items: order.total_items,
          table_label: order.table_label,
          table_token: tableToken || undefined,
          created_at: order.created_at || new Date().toISOString(),
        };
        const dedupedOrders = current.filter(
          (item) => item.public_order_token !== orderToken
        );
        const nextOrders = pruneRecentOrders([nextOrder, ...dedupedOrders]);
        window.localStorage.setItem(
          recentOrdersStorageKey,
          JSON.stringify(nextOrders)
        );
        return nextOrders;
      });
    },
    [menuSlug, recentOrdersStorageKey, tableToken]
  );

  const openRecentOrder = (order: RecentPublicOrder) => {
    if (!menuSlug) return;
    const orderTableToken = order.table_token || "";
    const tableQuery = orderTableToken
      ? `?t=${encodeURIComponent(orderTableToken)}`
      : "";
    navigate(`/menu/${menuSlug}/order/${order.public_order_token}${tableQuery}`);
  };

  const openCheckout = () => {
    if (!cartItems.length) {
      message.warning("Votre commande est vide.");
      return;
    }

    if (!canOrder) {
      message.warning("Les commandes en ligne ne sont pas actives pour ce menu.");
      return;
    }

    if (missingRequiredTable) {
      message.warning(
        hasInvalidTableToken
          ? "Ce QR code de table est invalide ou desactive."
          : "Un QR code de table valide est requis pour commander."
      );
      return;
    }

    setCartOpen(false);
    setCheckoutOpen(true);
  };

  const submitOrder = async (values: OrderFormValues) => {
    if (!menuSlug || !sessionId) return;
    setSubmitting(true);

    try {
      const idempotencyKey = createClientId();
      const response = await fetch(`${MENU_API}/${menuSlug}/orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Public-Session-ID": sessionId,
          "X-Idempotency-Key": idempotencyKey,
        },
        body: JSON.stringify({
          table_token: hasValidTableToken ? tableToken : undefined,
          service_mode: menuInfo?.default_service_mode,
          table_label: values.table_label,
          customer_name: values.customer_name,
          customer_phone: values.customer_phone,
          guest_count: values.guest_count || 1,
          notes_front: values.notes,
          items: cartItems.map((item) => ({
            product_id: item.product.id,
            quantity: item.quantity,
            notes: item.notes,
          })),
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.details || "Commande non envoyee.");
      }

      const order = data.order as PublicOrderResponse;
      rememberRecentOrder(order);
      clearCart();
      setCheckoutOpen(false);
      form.resetFields();

      const tableQuery = tableToken ? `?t=${encodeURIComponent(tableToken)}` : "";
      navigate(`/menu/${menuSlug}/order/${order.public_order_token}${tableQuery}`, {
        state: { order },
      });
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : "Erreur lors de l'envoi."
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!statusData?.order || !publicOrderToken) return;
    rememberRecentOrder(
      {
        ...statusData.order,
        public_order_token: publicOrderToken,
      },
      publicOrderToken
    );
  }, [publicOrderToken, rememberRecentOrder, statusData]);

  if (menuLoading && !menuInfo) {
    return (
      <Layout className="public-menu" style={themeStyle}>
        <Content className="public-menu__loading">
          <Spin size="large" />
          <Text>Chargement du menu...</Text>
        </Content>
      </Layout>
    );
  }

  if (loadError) {
    return (
      <Layout className="public-menu" style={themeStyle}>
        <Content className="public-menu__result">
          <Result
            status="404"
            title="Menu indisponible"
            subTitle={loadError}
            extra={
              <Button icon={<ReloadOutlined />} onClick={loadMenu}>
                Reessayer
              </Button>
            }
          />
        </Content>
      </Layout>
    );
  }

  if (isStatusPage) {
    return (
      <Layout className="public-menu public-menu--status" style={themeStyle}>
        <Content className="public-menu__status-shell">
          <OrderStatusView
            menuInfo={menuInfo}
            statusData={statusData}
            loading={statusLoading && !statusData}
            onRefresh={loadStatus}
            onBack={() =>
              navigate(
                `/menu/${menuSlug}${tableToken ? `?t=${encodeURIComponent(tableToken)}` : ""}`
              )
            }
          />
        </Content>
      </Layout>
    );
  }

  return (
    <Layout className="public-menu" style={themeStyle}>
      <header className="public-menu__topbar">
        <div className="public-menu__brand">
          <div className="public-menu__logo">
            {menuInfo?.logo_url || menuInfo?.company?.logo_url ? (
              <img
                src={menuInfo.logo_url || menuInfo.company?.logo_url || ""}
                alt={menuInfo?.menu_name || "Menu"}
              />
            ) : (
              <span>{productImageFallback(menuInfo?.menu_name || "M")}</span>
            )}
          </div>
          <div className="public-menu__brand-copy">
            <Text className="public-menu__eyebrow">
              {menuInfo?.warehouse?.name || "Menu digital"}
            </Text>
            <Title level={1}>{menuInfo?.menu_name}</Title>
          </div>
        </div>

        <Badge count={cartCount} size="small">
          <Button
            className="public-menu__cart-button"
            icon={<ShoppingCartOutlined />}
            onClick={() => setCartOpen(true)}
            aria-label="Commande"
          />
        </Badge>
      </header>

      <Content>
        <section className="public-menu__hero">
          <div className="public-menu__hero-copy">
            <Space size={[8, 8]} wrap className="public-menu__meta">
              {hasValidTableToken && (
                <Tag icon={<EnvironmentOutlined />} color="blue">
                  {menuInfo?.table?.label || "Table"}
                </Tag>
              )}
              {hasInvalidTableToken && (
                <Tag icon={<EnvironmentOutlined />} color="red">
                  QR table invalide
                </Tag>
              )}
              {menuInfo?.company?.phone && (
                <Tag icon={<PhoneOutlined />}>{menuInfo.company.phone}</Tag>
              )}
              {menuInfo?.auto_send_to_kitchen && (
                <Tag icon={<ClockCircleOutlined />} color="green">
                  Cuisine directe
                </Tag>
              )}
            </Space>

            {menuInfo?.description && (
              <Text className="public-menu__description">{menuInfo.description}</Text>
            )}

            {missingRequiredTable && (
              <Alert
                type="warning"
                showIcon
                className="public-menu__alert"
                message={
                  hasInvalidTableToken
                    ? "QR de table invalide ou desactive."
                    : "Commande a table indisponible depuis ce lien."
                }
              />
            )}

            <RecentOrdersPanel orders={recentOrders} onOpen={openRecentOrder} />
          </div>
        </section>

        <section className="public-menu__filters">
          <Input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            prefix={<SearchOutlined />}
            allowClear
            placeholder="Rechercher un plat, une boisson..."
            className="public-menu__search"
          />

          <div className="public-menu__category-rail">
            <Button
              className={selectedCategoryId === "all" ? "is-active" : ""}
              onClick={() => setSelectedCategoryId("all")}
            >
              Tout
            </Button>
            {categories.map((category) => (
              <Button
                key={category.id}
                className={selectedCategoryId === category.id ? "is-active" : ""}
                onClick={() => setSelectedCategoryId(category.id)}
              >
                {category.name}
              </Button>
            ))}
          </div>
        </section>

        <section className="public-menu__catalog">
          {productsLoading ? (
            <div className="public-menu__grid">
              {Array.from({ length: 6 }).map((_, index) => (
                <div className="public-menu__product-card" key={index}>
                  <Skeleton.Image active className="public-menu__skeleton-image" />
                  <Skeleton active paragraph={{ rows: 2 }} title />
                </div>
              ))}
            </div>
          ) : products.length ? (
            <div className="public-menu__grid">
              {products.map((product) => {
                const quantity = getCartQuantity(product.id);
                const unavailable = product.available_for_purchase === false;

                return (
                  <article
                    className={`public-menu__product-card ${
                      product.is_featured ? "is-featured" : ""
                    } ${unavailable ? "is-unavailable" : ""}`}
                    key={product.id}
                  >
                    <ProductThumb product={product} />
                    <div className="public-menu__product-main">
                      <div className="public-menu__product-head">
                        <div>
                          <Title level={2}>{product.name}</Title>
                          <Space size={[6, 6]} wrap>
                            {product.category_name && <Tag>{product.category_name}</Tag>}
                            {product.stock_status === "sur_commande" && (
                              <Tag color="orange">Sur commande</Tag>
                            )}
                            {product.is_featured && <Tag color="gold">Suggestion</Tag>}
                          </Space>
                        </div>
                        <Text className="public-menu__price">
                          {formatMoney(product.final_price)}
                        </Text>
                      </div>

                      {product.description && (
                        <Text className="public-menu__product-desc">
                          {product.description}
                        </Text>
                      )}

                      <div className="public-menu__product-actions">
                        {quantity > 0 ? (
                          <div className="public-menu__quantity">
                            <Button
                              icon={<MinusOutlined />}
                              onClick={() => updateCartQuantity(product, quantity - 1)}
                              aria-label="Reduire"
                            />
                            <span>{quantity}</span>
                            <Button
                              icon={<PlusOutlined />}
                              onClick={() => updateCartQuantity(product, quantity + 1)}
                              aria-label="Ajouter"
                            />
                          </div>
                        ) : (
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            disabled={unavailable}
                            onClick={() => updateCartQuantity(product, 1)}
                          >
                            Ajouter
                          </Button>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Aucun article disponible"
              className="public-menu__empty"
            />
          )}
        </section>
      </Content>

      {cartCount > 0 && (
        <div className="public-menu__bottom-bar">
          <div>
            <Text>{cartCount} article{cartCount > 1 ? "s" : ""}</Text>
            <strong>{formatMoney(cartTotal)}</strong>
          </div>
          <Button type="primary" icon={<ShoppingCartOutlined />} onClick={() => setCartOpen(true)}>
            Voir
          </Button>
        </div>
      )}

      <CartDrawer
        open={cartOpen}
        cartItems={cartItems}
        cartTotal={cartTotal}
        canCheckout={canOrder && !missingRequiredTable}
        onClose={() => setCartOpen(false)}
        onCheckout={openCheckout}
        onQuantityChange={updateCartQuantity}
        onClear={clearCart}
      />

      <Drawer
        className="public-menu__checkout"
        title="Finaliser la commande"
        placement="bottom"
        height="auto"
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={submitOrder}
          initialValues={{ guest_count: 1 }}
        >
          <div className="public-menu__checkout-grid">
            <Form.Item name="customer_name" label="Nom" rules={[]}>
              <Input prefix={<UserOutlined />} placeholder="Votre nom" />
            </Form.Item>

            <Form.Item
              name="customer_phone"
              label="Telephone"
              rules={
                menuInfo?.require_customer_phone
                  ? [{ required: true, message: "Telephone requis" }]
                  : []
              }
            >
              <Input prefix={<PhoneOutlined />} placeholder="Telephone" />
            </Form.Item>

            <Form.Item name="guest_count" label="Couverts">
              <InputNumber min={1} max={99} style={{ width: "100%" }} />
            </Form.Item>

            {needsManualTableLabel && (
              <Form.Item
                name="table_label"
                label="Table ou emplacement"
                rules={[
                  {
                    required: true,
                    message: "Indiquez votre table ou emplacement.",
                  },
                ]}
              >
                <Input
                  prefix={<EnvironmentOutlined />}
                  placeholder="Ex: Table 4, terrasse, comptoir..."
                />
              </Form.Item>
            )}

            <Form.Item name="notes" label="Note">
              <TextArea
                autoSize={{ minRows: 3, maxRows: 5 }}
                placeholder="Cuisson, allergie, precision..."
              />
            </Form.Item>
          </div>

          <div className="public-menu__checkout-total">
            <div>
              <Text>Total</Text>
              <strong>{formatMoney(cartTotal)}</strong>
            </div>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={submitting}
              icon={<SendOutlined />}
            >
              Envoyer
            </Button>
          </div>
        </Form>
      </Drawer>
    </Layout>
  );
};

type CartDrawerProps = {
  open: boolean;
  cartItems: CartItem[];
  cartTotal: number;
  canCheckout: boolean;
  onClose: () => void;
  onCheckout: () => void;
  onQuantityChange: (product: MenuProduct, quantity: number) => void;
  onClear: () => void;
};

type RecentOrdersPanelProps = {
  orders: RecentPublicOrder[];
  onOpen: (order: RecentPublicOrder) => void;
};

const RecentOrdersPanel: React.FC<RecentOrdersPanelProps> = ({
  orders,
  onOpen,
}) => {
  if (!orders.length) return null;

  return (
    <div className="public-menu__tracking-card">
      <div className="public-menu__tracking-head">
        <div>
          <Text strong>Suivre ma commande</Text>
          <Text type="secondary">Commandes recentes depuis cet appareil.</Text>
        </div>
      </div>
      <List
        size="small"
        dataSource={orders.slice(0, 3)}
        renderItem={(order) => {
          return (
            <List.Item
              actions={[
                <Button type="link" onClick={() => onOpen(order)} key="track">
                  Suivre
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Space size={[6, 6]} wrap>
                    <Text strong>{order.order_number}</Text>
                  </Space>
                }
                description={
                  <Space size={[8, 4]} wrap>
                    {order.table_label && (
                      <Text type="secondary">{order.table_label}</Text>
                    )}
                    <Text type="secondary">{formatMoney(order.total)}</Text>
                  </Space>
                }
              />
            </List.Item>
          );
        }}
      />
    </div>
  );
};

const CartDrawer: React.FC<CartDrawerProps> = ({
  open,
  cartItems,
  cartTotal,
  canCheckout,
  onClose,
  onCheckout,
  onQuantityChange,
  onClear,
}) => (
  <Drawer
    title="Votre commande"
    placement="right"
    width="min(420px, 100vw)"
    open={open}
    onClose={onClose}
    className="public-menu__cart-drawer"
    extra={
      cartItems.length > 0 ? (
        <Button type="text" danger icon={<DeleteOutlined />} onClick={onClear}>
          Vider
        </Button>
      ) : null
    }
  >
    {cartItems.length ? (
      <>
        <List
          dataSource={cartItems}
          renderItem={(item) => (
            <List.Item className="public-menu__cart-line">
              <ProductThumb product={item.product} size="small" />
              <div className="public-menu__cart-line-main">
                <Text strong>{item.product.name}</Text>
                <Text type="secondary">
                  {formatMoney(item.product.final_price)} x {item.quantity}
                </Text>
                <div className="public-menu__quantity public-menu__quantity--compact">
                  <Button
                    icon={<MinusOutlined />}
                    onClick={() => onQuantityChange(item.product, item.quantity - 1)}
                  />
                  <span>{item.quantity}</span>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => onQuantityChange(item.product, item.quantity + 1)}
                  />
                  <Button
                    danger
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={() => onQuantityChange(item.product, 0)}
                  />
                </div>
              </div>
              <Text strong>{formatMoney(item.quantity * item.product.final_price)}</Text>
            </List.Item>
          )}
        />

        <div className="public-menu__cart-footer">
          <div>
            <Text>Total</Text>
            <strong>{formatMoney(cartTotal)}</strong>
          </div>
          {!canCheckout && (
            <Alert
              type="warning"
              showIcon
              message="Commande indisponible depuis ce lien."
            />
          )}
          <Button
            type="primary"
            size="large"
            block
            icon={<SendOutlined />}
            onClick={onCheckout}
            disabled={!canCheckout}
          >
            Commander
          </Button>
        </div>
      </>
    ) : (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Commande vide" />
    )}
  </Drawer>
);

type OrderStatusViewProps = {
  menuInfo: PublicMenuInfo | null;
  statusData: PublicOrderStatus | null;
  loading: boolean;
  onRefresh: () => void;
  onBack: () => void;
};

const OrderStatusView: React.FC<OrderStatusViewProps> = ({
  menuInfo,
  statusData,
  loading,
  onRefresh,
  onBack,
}) => {
  const order = statusData?.order;
  const publicStage = getPublicOrderStage(
    order?.flow_status,
    order?.kitchen_status
  );
  const publicStatus = publicStageMeta[publicStage];
  const stepsStatus =
    publicStage === "blocked"
      ? "error"
      : publicStage === "completed"
      ? "finish"
      : "process";
  const isCancelled = publicStage === "cancelled";

  return (
    <div className="public-menu__status-card">
      <Button icon={<ArrowLeftOutlined />} type="text" onClick={onBack}>
        Menu
      </Button>

      {loading ? (
        <div className="public-menu__status-loading">
          <Spin />
          <Text>Chargement...</Text>
        </div>
      ) : order ? (
        <>
          <div className="public-menu__status-head">
            <div>
              <Text className="public-menu__eyebrow">
                {menuInfo?.menu_name || "Menu digital"}
              </Text>
              <Title level={1}>Commande {order.order_number}</Title>
              <Space size={[8, 8]} wrap>
                {order.table_label && (
                  <Tag icon={<EnvironmentOutlined />}>{order.table_label}</Tag>
                )}
                <Tag color={publicStatus.color}>{publicStatus.label}</Tag>
              </Space>
            </div>
            <Button icon={<ReloadOutlined />} onClick={onRefresh}>
              Actualiser
            </Button>
          </div>

          {isCancelled ? (
            <Result
              status="warning"
              title="Commande annulee"
              subTitle={publicStatus.description}
            />
          ) : (
            <>
              <Alert
                type={publicStatus.color === "red" ? "warning" : "info"}
                showIcon
                className="public-menu__status-note"
                message={publicStatus.description}
              />
              <Steps
                current={publicStatus.step}
                status={stepsStatus}
                items={[
                  { title: "Recue", icon: <CheckCircleFilled /> },
                  { title: "Cuisine", icon: <ClockCircleOutlined /> },
                  { title: "Prete", icon: <CheckCircleFilled /> },
                  { title: "Terminee", icon: <CheckCircleFilled /> },
                ]}
              />
            </>
          )}

          <div className="public-menu__status-summary">
            <div>
              <Text>Total</Text>
              <strong>{formatMoney(order.total)}</strong>
            </div>
            <div>
              <Text>Articles</Text>
              <strong>{order.total_items}</strong>
            </div>
          </div>

          <List
            className="public-menu__status-items"
            dataSource={order.items}
            renderItem={(item) => (
              <List.Item>
                <div>
                  <Text strong>{item.product_name}</Text>
                  <Text type="secondary">
                    {item.quantity} {item.unit_short_name || ""}
                  </Text>
                </div>
                <Text>{formatMoney(item.subtotal)}</Text>
              </List.Item>
            )}
          />
        </>
      ) : (
        <Result
          status="404"
          title="Commande introuvable"
          extra={
            <Button icon={<ReloadOutlined />} onClick={onRefresh}>
              Reessayer
            </Button>
          }
        />
      )}
    </div>
  );
};

export default PublicMenu;
